import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { listaPrecioService, ListaPrecio } from '../../services/listaPrecioService';
import ListaPrecioForm from '../../components/ListasPrecios/ListaPrecioForm';
import ListaPrecioDetail from '../../components/ListasPrecios/ListaPrecioDetail';

const ListasPreciosList = () => {
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingLista, setEditingLista] = useState<ListaPrecio | null>(null);
  const [selectedLista, setSelectedLista] = useState<ListaPrecio | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['listas-precios', page],
    () => listaPrecioService.getAll(page, 20)
  );

  const deleteMutation = useMutation(listaPrecioService.delete, {
    onSuccess: (data) => {
      toast.success(data.message || 'Lista eliminada correctamente');
      queryClient.invalidateQueries(['listas-precios']);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al eliminar lista');
    }
  });

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de eliminar esta lista de precios?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (lista: ListaPrecio) => {
    setEditingLista(lista);
    setShowForm(true);
  };

  const handleView = (lista: ListaPrecio) => {
    setSelectedLista(lista);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingLista(null);
  };

  const handleCloseDetail = () => {
    setSelectedLista(null);
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Listas de Precios</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
        >
          + Nueva Lista
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.listas.map((lista) => (
                <tr key={lista.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {lista.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lista.items?.length || 0} productos
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${lista.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {lista.isActive ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleView(lista)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Ver
                    </button>
                    <button
                      onClick={() => handleEdit(lista)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(lista.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data?.data.pagination && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Mostrando {((page - 1) * 20) + 1} a {Math.min(page * 20, data.data.pagination.total)} de {data.data.pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= data.data.pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <ListaPrecioForm
          lista={editingLista}
          onClose={handleCloseForm}
        />
      )}

      {selectedLista && (
        <ListaPrecioDetail
          lista={selectedLista}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
};

export default ListasPreciosList;

